/**
 * Violet Descent (Phase 2E): the challenge track.
 *
 * Designed safe-route first; every alternative is optional. All routes are
 * completable with baseline movement (experiments off).
 *
 *  1 Intro (forgiving)   wide stone hall, one 0.8 m step, no hazards     → CP1
 *  2 Momentum            15° descent between greenstone rails (8.8 m drop)
 *  3 Curve               quarter turn west; the outer marble wall is a
 *                        concave curve you can slide along without losing speed → CP2
 *  4 Fork                north lane = SAFE: precision path over a lethal floor
 *                        (zigzag, low bars, islands)
 *                        south lane = MOMENTUM: downhill run-up, 9 m lethal gap
 *                        (needs ≈ 13 m/s with a jump: carry the descent speed)
 *  5 Junction            → CP3, 3 m below the gap take-off so the landing keeps
 *                        speed; finish terrace 4 m up
 *                        SAFE: five 0.8 m rises
 *                        SHORTCUT (skill): short 30° kicker; a jump at the lip adds
 *                        to the ramp's vertical speed (additive jump) and clears the
 *                        terrace edge only when fast (≳ 14 m/s)
 *
 * Combination opportunity: descent speed → curve wall slide → gap jump →
 * slope landing → kicker, chained without stopping.
 *
 * Coordinates: metres, +Y up; the route runs −Z, then −X.
 */
import type { LevelData, LightDef, SolidDef, TriggerDef } from '../world/LevelData';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';
import { arcWall, ramp, tiledBox } from '../world/LevelBuilders';

type V3 = [number, number, number];
const box = (id: string, c: V3, half: V3, material: string): SolidDef => ({ id, shape: { type: 'box', halfExtents: half }, position: c, material });
const slab = (id: string, x0: number, x1: number, z0: number, z1: number, top: number, material: string, thickness = 0.5): SolidDef[] =>
  tiledBox(id, [(x0 + x1) / 2, top - thickness / 2, (z0 + z1) / 2], [Math.abs(x1 - x0) / 2, thickness / 2, Math.abs(z1 - z0) / 2], material);

/** Track-only accent materials: safe surfaces that carry the cyan / violet route language. */
const MATERIALS: SurfaceMaterialDef[] = [
  { id: 'accent.cyan', kind: 'safe', behaviors: [], visual: { color: 0x0f5560, emissive: 0x39e6ff, emissiveIntensity: 0.55, roughness: 0.5, pattern: 'glyph', opacity: 1 } },
  { id: 'accent.violet', kind: 'safe', behaviors: [], visual: { color: 0x3a2366, emissive: 0xa77bff, emissiveIntensity: 0.5, roughness: 0.5, pattern: 'glyph', opacity: 1 } },
];

const DESCENT_TOP = 0.8;
const LOW = -8;
const GAP_Y = LOW - 3;
const GAP_NEAR_X = -36;
const GAP_FAR_X = -45;
const JUNCTION = GAP_Y - 3;
const TERRACE = JUNCTION + 4;
/** A short 30° kicker turns approach speed into vertical speed (v·sin30°) without a
 *  long uphill that would bleed it. Chosen by sweep: from run speed up to 12 m/s
 *  the jump falls short of the terrace edge; from 14 m/s it clears it cleanly. */
const KICKER_START_X = -68;
const KICKER_LIP = JUNCTION + 1.2;
const KICKER_END_X = KICKER_START_X - 2.1;
const TERRACE_FACE_X = KICKER_END_X - 4;

/** Key heights and positions, exported so route tests follow the level when it is retuned. */
export const VIOLET_DESCENT_GEOMETRY = Object.freeze({
  gapTakeoffY: GAP_Y,
  gapFarEdgeX: GAP_FAR_X,
  junctionY: JUNCTION,
  terraceY: TERRACE,
  kickerLipX: KICKER_END_X,
  terraceFaceX: TERRACE_FACE_X,
});

// 1 · Intro -------------------------------------------------------------------
const intro: SolidDef[] = [
  ...slab('intro.floor', -6, 6, 8, -30, 0, 'safe.stone'),
  ...slab('intro.step', -6, 6, -14, -30, DESCENT_TOP, 'safe.marble', 0.8),
  ...tiledBox('intro.wallL', [-6.5, 1, -11], [0.5, 2, 19], 'safe.greenstone'),
  ...tiledBox('intro.wallR', [6.5, 1, -11], [0.5, 2, 19], 'safe.greenstone'),
  box('intro.stepEdge', [0, DESCENT_TOP - 0.05, -14.05], [6, 0.05, 0.05], 'accent.cyan'),
];

// 2 · Descent ------------------------------------------------------------------
const descent: SolidDef[] = [
  ...ramp('descent', [0, DESCENT_TOP, -30], [0, LOW, -62], 10, 1, 'safe.marble'),
  ...tiledBox('descent.railL', [-5.5, -3, -46], [0.5, 6, 16], 'safe.greenstone'),
  ...tiledBox('descent.railR', [5.5, -3, -46], [0.5, 6, 16], 'safe.greenstone'),
  ...slab('descent.bottom', -5, 5, -61.5, -80, LOW, 'safe.stone'),
];

// 3 · Curve ---------------------------------------------------------------------
const curve: SolidDef[] = [
  ...slab('curve.floor', -22, 5, -80, -94, LOW, 'safe.stone'),
  ...slab('north.entry', -22, -30, -80, -87, LOW, 'safe.stone'),
  ...arcWall('curve.outer', [-9, LOW, -80], 14, 0, 90, 14, 4, 0.5, 'accent.violet'),
  ...tiledBox('curve.wallE', [5.5, LOW + 2, -71], [0.5, 2, 9], 'safe.greenstone'),
  ...tiledBox('curve.wallW', [-5.5, LOW + 2, -71], [0.5, 2, 9], 'safe.greenstone'),
  // North boundary of the westbound corridor (inner side of the turn).
  ...tiledBox('corridor.wallN', [-41.75, LOW + 2, -79.75], [36.25, 2, 0.25], 'safe.greenstone'),
  ...tiledBox('corridor.wallS', [-43.5, LOW - 1.5, -94.25], [34.5, 5.5, 0.25], 'safe.greenstone'),
  // Lane divider from the end of the curve to the junction.
  ...tiledBox('fork.divider', [-48, LOW - 3, -87], [20, 6, 0.25], 'safe.greenstone'),
  box('fork.signN', [-27.8, LOW + 3.1, -83.5], [0.15, 0.1, 3.4], 'accent.cyan'),
  box('fork.signS', [-27.8, LOW + 3.1, -90.5], [0.15, 0.1, 3.4], 'accent.violet'),
];

// 4a · North lane: SAFE precision section over a lethal floor --------------------
// Islands sit one running jump (≈ 5.9 m at run speed) apart, so the section is
// a rhythm: land, jump again before the 2.4 m island ends.
const north: SolidDef[] = [
  ...slab('north.lethal', -30, -64, -80, -87, LOW - 2, 'lethal.basic'),
  ...slab('north.p1', -30, -36.2, -82.5, -84.5, LOW, 'safe.stone'),
  ...slab('north.p2', -36, -44, -83.9, -85.3, LOW, 'safe.stone'),
  box('north.bar', [-38.6, LOW + 0.25, -84.6], [0.15, 0.25, 0.7], 'lethal.basic'),
  ...slab('north.i1', -48.3, -50.7, -82.3, -84.3, LOW, 'safe.stone'),
  ...slab('north.i2', -54.2, -56.6, -83.2, -85.2, LOW, 'safe.stone'),
  ...slab('north.p3', -60, -67, -82.5, -84.5, LOW, 'safe.stone'),
];

// 4b · South lane: MOMENTUM gap. A 14° run-up converts 3 m of height into speed;
// the 9 m gap then needs ≈ 13 m/s with a jump at the lip.
const south: SolidDef[] = [
  ...ramp('south.runup', [-22, LOW, -90.5], [-34, GAP_Y, -90.5], 7, 0.6, 'safe.marble'),
  // Flat take-off: jumping on the slope itself would subtract the slope's downward velocity.
  ...slab('south.takeoff', -33.5, GAP_NEAR_X, -87, -94, GAP_Y, 'safe.stone'),
  ...slab('south.lethal', GAP_NEAR_X, GAP_FAR_X, -87, -94, GAP_Y - 2, 'lethal.basic'),
  box('south.lip', [GAP_NEAR_X + 0.1, GAP_Y - 0.05, -90.5], [0.1, 0.05, 3.5], 'accent.violet'),
  ...ramp('south.landing', [GAP_FAR_X, GAP_Y, -90.5], [-64, JUNCTION, -90.5], 7, 0.6, 'safe.stone'),
];

// 5 · Junction, stairs, kicker, terrace -------------------------------------------
// Five 0.8 m rises (four steps, then the terrace) between the kicker start and the terrace face.
const STEP_RISE = (TERRACE - JUNCTION) / 5;
const STEP_TOPS = [1, 2, 3, 4].map((k) => JUNCTION + k * STEP_RISE);
const STEP_LEN = (KICKER_START_X - TERRACE_FACE_X) / 4;
const junction: SolidDef[] = [
  ...slab('junction.floor', -63, TERRACE_FACE_X, -80, -94, JUNCTION, 'safe.stone'),
  ...STEP_TOPS.map((top, i) => box(`stairs.${i}`, [KICKER_START_X - (i + 0.5) * STEP_LEN, (top + JUNCTION) / 2, -82], [STEP_LEN / 2, (top - JUNCTION) / 2, 2], i % 2 ? 'safe.marble' : 'safe.stone')),
  ...ramp('kicker', [KICKER_START_X, JUNCTION, -90], [KICKER_END_X, KICKER_LIP, -90], 4, 0.6, 'accent.cyan'),
  box('terrace', [TERRACE_FACE_X - 10, (TERRACE + JUNCTION) / 2, -87], [10, (TERRACE - JUNCTION) / 2, 7], 'safe.marble'),
  box('terrace.edge', [TERRACE_FACE_X - 0.05, TERRACE - 0.05, -87], [0.05, 0.05, 7], 'accent.cyan'),
  box('terrace.back', [TERRACE_FACE_X - 20.5, TERRACE + 3, -87], [0.5, 3, 7.5], 'safe.greenstone'),
  ...tiledBox('junction.wallS', [-80, JUNCTION + 4, -94.25], [17, 4, 0.25], 'safe.greenstone'),
  ...tiledBox('junction.wallN', [-80, JUNCTION + 4, -79.75], [17, 4, 0.25], 'safe.greenstone'),
];

const triggers: TriggerDef[] = [
  { id: 'start', kind: 'start', center: [0, 1, 2], halfExtents: [6, 2, 0.5], material: 'trigger.start' },
  {
    id: 'cp.descent',
    kind: 'checkpoint',
    center: [0, DESCENT_TOP + 2, -27],
    halfExtents: [6, 2, 0.5],
    material: 'trigger.checkpoint',
    checkpointIndex: 0,
    spawn: { position: [0, DESCENT_TOP + 0.02, -25], yawDeg: 0 },
  },
  {
    // North lane only: a failed momentum attempt restarts from the top of the
    // descent, so the run-up speed is available again.
    id: 'cp.fork',
    kind: 'checkpoint',
    center: [-26, LOW + 2, -83.5],
    halfExtents: [0.5, 2, 3.5],
    material: 'trigger.checkpoint',
    checkpointIndex: 1,
    spawn: { position: [-27, LOW + 0.02, -83.5], yawDeg: 90 },
  },
  {
    id: 'cp.junction',
    kind: 'checkpoint',
    center: [-67.5, JUNCTION + 5, -87],
    halfExtents: [0.5, 5, 7],
    material: 'trigger.checkpoint',
    checkpointIndex: 2,
    spawn: { position: [-70, JUNCTION + 0.02, -86], yawDeg: 90 },
  },
  // Tall enough that a high shortcut arc still crosses it.
  { id: 'finish', kind: 'finish', center: [TERRACE_FACE_X - 15, TERRACE + 4, -87], halfExtents: [0.5, 4, 7], material: 'trigger.finish' },
];

const lantern = (x: number, y: number, z: number): LightDef => ({ position: [x, y, z], color: 0xffa64d, intensity: 22, distance: 24 });

export const VIOLET_DESCENT: LevelData = {
  id: 'violet-descent',
  name: 'Violet Descent',
  version: 1,
  materials: MATERIALS,
  solids: [...intro, ...descent, ...curve, ...north, ...south, ...junction],
  triggers,
  start: { position: [0, 0.02, 5], yawDeg: 0 },
  killPlaneY: -30,
  lights: [
    lantern(-5, 3, 0),
    lantern(5, 3, -20),
    lantern(0, 2, -45),
    lantern(0, -4, -66),
    lantern(-6, -4, -90),
    lantern(-2, -4.5, -83),
    lantern(5, -4.5, -91),
    lantern(-30, -6, -92),
    lantern(-22, -4, -82),
    lantern(-38, -4, -82),
    lantern(-55, -4, -82),
    lantern(-40, -4, -92),
    lantern(-70, JUNCTION + 4, -87),
    lantern(-86, TERRACE + 3, -87),
  ],
  labels: [
    { text: 'SAFE ROUTE', position: [-28, LOW + 4.5, -83.5] },
    { text: 'MOMENTUM', position: [-28, LOW + 4.5, -90.5] },
    { text: 'SHORTCUT ≳14 m/s', position: [-70, JUNCTION + 4.5, -90] },
  ],
  waypoints: [
    { name: 'Start', spawn: { position: [0, 0.02, 5], yawDeg: 0 } },
    { name: 'Descent', spawn: { position: [0, DESCENT_TOP + 0.02, -25], yawDeg: 0 } },
    { name: 'Fork', spawn: { position: [-27, LOW + 0.02, -83.5], yawDeg: 90 } },
    { name: 'Junction', spawn: { position: [-70, JUNCTION + 0.02, -86], yawDeg: 90 } },
  ],
};
